function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
import { ApolloLink, Observable } from "@apollo/client";
import DataNetworkError from "../graphql/DataNetworkError";
import { extractPrefixFromVariableName } from "../queryUtils/prefixes";
import { logHttpError, logHttpSuccess } from "../telemetry/logHttp";
import loadId from "../../shared/loadId";
import { REQUEST_SOURCE_HEADER, getRequestSourceHeader, getRequestSourceInformation } from "../../shared/requestSource";
import { EXCLUDE_FROM_SENTRY } from "../../shared/telemetry/observability";
function now() {
    return typeof performance !== 'undefined' ? performance.now() : Date.now();
}
function getOperationInformation(operation) {
    const context = operation.getContext();
    return getRequestSourceInformation({
        service: context.service,
        project: context.queryProject,
        component: context.component,
        operation: operation.operationName
    });
}
export default function createObservabilityLink() {
    return new ApolloLink(function observabilityLink(operation, forward) {
        const context = operation.getContext();
        const { headers } = context;
        const info = getOperationInformation(operation);
        const xRequestSource = getRequestSourceHeader(info);
        operation.setContext({
            headers: _object_spread({
                [REQUEST_SOURCE_HEADER]: xRequestSource
            }, headers)
        });
        return new Observable(function observabilityObserver(observer) {
            const start = now();
            const subscription = forward(operation).subscribe({
                next: function observabilityLinkNext(result) {
                    const duration = Math.round(now() - start);
                    if (result.errors) {
                        result.errors.forEach((error)=>{
                            error[EXCLUDE_FROM_SENTRY] = true;
                            const gqlError = error;
                            const errorInfo = {
                                message: gqlError.message,
                                path: gqlError.path,
                                actionId: gqlError.extensions.actionId,
                                traceId: gqlError.extensions.traceId,
                                requestId: gqlError.extensions.unstable_requestId,
                                code: gqlError.extensions.error.code,
                                status: gqlError.extensions.error.status
                            };
                            processError(operation, duration, errorInfo);
                        });
                    } else {
                        processSuccess(operation, duration);
                    }
                    return observer.next(result);
                },
                error: function observabilityLinkError(error) {
                    const duration = Math.round(now() - start);
                    let errorInfo;
                    if (error instanceof DataNetworkError) {
                        errorInfo = {
                            message: error.message,
                            path: null,
                            actionId: error.extensions.actionId,
                            traceId: error.extensions.traceId,
                            requestId: error.extensions.unstable_requestId,
                            code: error.extensions.error.code,
                            status: error.extensions.error.status
                        };
                    } else {
                        const errorMessage = typeof error === 'object' && error && 'message' in error && typeof error.message === 'string' ? error.message : 'Unknown error';
                        errorInfo = {
                            message: errorMessage,
                            path: null,
                            actionId: null,
                            traceId: null,
                            requestId: null,
                            code: 'could_not_get_error_code',
                            status: -1
                        };
                    }
                    processError(operation, duration, errorInfo);
                    error[EXCLUDE_FROM_SENTRY] = true;
                    return observer.error(error);
                },
                complete: function observabilityLinkComplete() {
                    return observer.complete();
                }
            });
            return function observabilityLinkUnsubscribe() {
                if (subscription) {
                    subscription.unsubscribe();
                }
            };
        });
    });
}
const FALLBACK_OPERATION_NAME = 'CouldNotGetOperationName';
function getErrorParams(graphqlOperation, error) {
    var _expectedErrors_prefix;
    const errorCode = error.code;
    if (graphqlOperation.type === 'MUTATION' || graphqlOperation.type === 'STANDALONE_QUERY') {
        const operationName = graphqlOperation.operationName;
        const expectedErrors = graphqlOperation.expectedErrors;
        const isExpected = expectedErrors.includes(errorCode);
        return {
            operationName,
            isExpected
        };
    }
    const prefix = getPrefixFromPath(error.path);
    const operationName = graphqlOperation.operationNames[prefix] || FALLBACK_OPERATION_NAME;
    const expectedErrors = graphqlOperation.expectedErrors;
    const isExpected = (_expectedErrors_prefix = expectedErrors[prefix]) === null || _expectedErrors_prefix === void 0 ? void 0 : _expectedErrors_prefix.includes(errorCode);
    return {
        operationName,
        isExpected
    };
}
function processError(operation, duration, error) {
    const context = operation.getContext();
    const graphQlOperation = context.graphQlOperation;
    if (!graphQlOperation || !context.observabilityFns) {
        return;
    }
    const errorCode = error.code;
    const { operationName, isExpected } = getErrorParams(graphQlOperation, error);
    const reportParams = {
        service: context.service || '<unknown>',
        project: context.queryProject || '<unknown>',
        // Back-end error code from the API error response or GraphQL error result, e.g. 'rate_limit_exceeded'
        errorCode,
        errorMessage: error.message,
        errorExpected: isExpected,
        errorPath: (error.path || []).join('.'),
        // The GraphQL operation name, e.g. PaymentsFragment
        operationName,
        // The HTTP status code of the API response.
        networkDuration: duration,
        networkStatus: error.status,
        networkActionId: error.actionId,
        networkRequestId: error.requestId,
        networkTraceId: error.traceId,
        loadId
    };
    logHttpError('GraphQL', context.observabilityFns, reportParams);
}
function processSuccess(operation, duration) {
    const context = operation.getContext();
    const graphQlOperation = context.graphQlOperation;
    if (!context.observabilityFns || !graphQlOperation) {
        return;
    }
    const successOperationNames = 'operationName' in graphQlOperation ? [
        graphQlOperation.operationName
    ] : Object.values(graphQlOperation.operationNames);
    const observabilityFns = context.observabilityFns;
    successOperationNames.forEach((operationName)=>{
        const reportParams = {
            service: context.service || '<unknown>',
            project: context.queryProject || '<unknown>',
            operationName,
            networkDuration: duration,
            loadId
        };
        logHttpSuccess('GraphQL', observabilityFns, reportParams);
    });
}
function getPrefixFromPath(path) {
    const firstSegment = path === null || path === void 0 ? void 0 : path[0];
    return typeof firstSegment === 'string' ? extractPrefixFromVariableName(firstSegment) : 'not-found-prefix';
}

//# sourceMappingURL=createObservabilityLink.js.map